import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { PlpRoot } from '@core/Entities/Search/PlpRoot.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import type {
  CollapsibleInterlinkingItem,
  CollapsibleInterlinkingProps,
} from '@page-builder/Components/InterLinking/CollapsibleInterLinking/CollapsibleInterlinking.entity';

import type { ExtendedAggregations, SectionConfig } from '../VehicleInterlinking.entities';
import {
  MAKE_INTERLINKING_SECTIONS,
  MAKE_MODEL_INTERLINKING_SECTIONS,
  PLP_VEHICLE_INTERLINKING_SECTIONS,
} from '../VehicleInterlinkingSectionsConfigs';

type SectionOption = {
  id: string;
  count: number;
  label: string;
  labelKey?: string;
};
export const getAllOptions = (options: SectionOption[]): SectionOption[] => {
  return options.filter(option => option.count > 0).sort((a, b) => b.count - a.count);
};

export const getMessageDictionary = (
  config: SectionConfig,
  bodyTypeMessages: Record<string, string>,
  fuelTypeMessages: Record<string, string>,
  gearBoxMessages: Record<string, string>,
  colorMessages: Record<string, string>,
): Record<string, string> => {
  switch (config.messageKey) {
    case 'body-types':
      return bodyTypeMessages;
    case 'fuel-types':
      return fuelTypeMessages;
    case 'transmissions':
      return gearBoxMessages;
    case 'colors':
      return colorMessages;
    default:
      return {};
  }
};

const createLinkBlock = (
  config: SectionConfig,
  options: SectionOption[],
  messages: Record<string, string>,
  make: string | undefined,
  modelWithMake: string | undefined,
  _locale: Locale,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
  root: PlpRoot,
): CollapsibleInterlinkingItem['linkBlocks'][number] | null => {
  const allOptions = getAllOptions(options);
  if (allOptions.length === 0) return null;

  const cta = allOptions.map(option => {
    const displayName = config.getDisplayName(option, messages);
    const filters = config.createFilters(make, modelWithMake, option.id);
    return {
      ctaText: config.formatCtaText(makeDisplayName, modelDisplayName, displayName),
      link: createPathFromFilters(filters, root),
    };
  });

  return {
    heading: messages[config.headingKey],
    // Temporary fix to avoid breaking changes to the PLP.spec tests
    dataTestId: config.key === 'models' ? 'plp-all-models' : `${config.key}-interlinking-section`,
    cta,
  };
};

export const transformToInterlinkingContent = (
  aggregations: ExtendedAggregations,
  make: string | undefined,
  modelWithMake: string | undefined,
  locale: Locale,
  messages: Record<string, string>,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
  root: PlpRoot = 'used',
): CollapsibleInterlinkingProps => {
  const configs = modelWithMake
    ? MAKE_MODEL_INTERLINKING_SECTIONS
    : make
      ? MAKE_INTERLINKING_SECTIONS
      : PLP_VEHICLE_INTERLINKING_SECTIONS;

  const linkBlocks: CollapsibleInterlinkingItem['linkBlocks'] = configs
    .map(config =>
      createLinkBlock(
        config,
        config.getOptions(aggregations, make, modelWithMake),
        messages,
        make,
        modelWithMake,
        locale,
        makeDisplayName,
        modelDisplayName,
        root,
      ),
    )
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    interlinkingItems: [
      {
        heading: make ? messages.moreOptions : messages.moreUsedCarOptions,
        linkBlocks,
      },
    ],
  };
};
